import React, { useState } from 'react';
import { Upload, Video, Image as ImageIcon } from 'lucide-react';

// Max video size: 50MB
const MAX_VIDEO_SIZE_MB = 50;
const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;

interface MediaDropZoneProps {
  onMediaDrop: (file: File, type: 'image' | 'video') => void;
  isUploading: boolean;
  label?: string;
  showAlways?: boolean;
  compact?: boolean;
  acceptVideo?: boolean; // Enable video upload
  currentMediaType?: 'image' | 'video' | null;
}

export const MediaDropZone: React.FC<MediaDropZoneProps> = ({
  onMediaDrop,
  isUploading,
  label = 'Drop image or video here',
  showAlways = false,
  compact = false,
  acceptVideo = true,
  currentMediaType = null,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateAndProcessFile = (file: File) => {
    setError(null);

    // Check if it's an image
    if (file.type.startsWith('image/')) {
      onMediaDrop(file, 'image');
      return;
    }

    // Check if it's a video
    if (file.type.startsWith('video/') && acceptVideo) {
      // Validate video size
      if (file.size > MAX_VIDEO_SIZE_BYTES) {
        setError(
          `Video must be under ${MAX_VIDEO_SIZE_MB}MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)}MB`
        );
        return;
      }

      // Validate video format (accept common formats)
      const validFormats = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
      if (!validFormats.includes(file.type)) {
        setError('Please use MP4, WebM, or MOV format');
        return;
      }

      onMediaDrop(file, 'video');
      return;
    }

    // Invalid file type
    if (acceptVideo) {
      setError('Please drop an image or video file');
    } else {
      setError('Please drop an image file');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setError(null);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      validateAndProcessFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      validateAndProcessFile(files[0]);
    }
  };

  const handleClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptVideo
      ? 'image/*,video/mp4,video/webm,video/ogg,video/quicktime'
      : 'image/*';
    input.onchange = (e) => handleFileSelect(e as any);
    input.click();
  };

  const getLabel = () => {
    if (acceptVideo) {
      return label || 'Drop image or video';
    }
    return label || 'Drop image here';
  };

  if (!showAlways && !isDragging && !isUploading) {
    return (
      <div
        onDragOver={handleDragOver}
        className={`absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-200 ${
          compact ? 'rounded-full' : ''
        }`}
      >
        <div
          className={`w-full h-full bg-black bg-opacity-70 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer ${
            compact ? 'rounded-full' : ''
          }`}
          onClick={handleClick}
        >
          <div className="text-center text-white">
            <div className="flex justify-center gap-2 mb-2">
              <ImageIcon className={`${compact ? 'w-5 h-5' : 'w-8 h-8'}`} />
              {acceptVideo && <Video className={`${compact ? 'w-5 h-5' : 'w-8 h-8'}`} />}
            </div>
            <p className={`${compact ? 'text-xs' : 'text-sm'} font-semibold`}>{getLabel()}</p>
            {acceptVideo && !compact && (
              <p className="text-xs text-gray-300 mt-1">Video max: {MAX_VIDEO_SIZE_MB}MB</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={!isUploading ? handleClick : undefined}
      className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-all ${
        showAlways
          ? `border-2 border-dashed ${
              isDragging
                ? 'border-yellow-400 bg-yellow-400 bg-opacity-20'
                : 'border-gray-300 bg-gray-800 bg-opacity-60'
            }`
          : `bg-black bg-opacity-70 ${isDragging ? 'bg-opacity-80 ring-2 ring-yellow-400' : ''}`
      } ${compact ? 'rounded-full' : ''}`}
    >
      <div className="text-center text-white">
        {isUploading ? (
          <>
            <div
              className={`animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-2`}
            />
            <p className="text-sm font-semibold">Uploading...</p>
          </>
        ) : error ? (
          <>
            <p className="text-sm font-semibold text-red-400 mb-2">{error}</p>
            <p className="text-xs text-gray-300">Click to try again</p>
          </>
        ) : (
          <>
            <div className={`flex justify-center gap-2 mb-2 ${isDragging ? 'animate-bounce' : ''}`}>
              <ImageIcon className={`${compact ? 'w-5 h-5' : 'w-8 h-8'}`} />
              {acceptVideo && <Video className={`${compact ? 'w-5 h-5' : 'w-8 h-8'}`} />}
            </div>
            <p className={`${compact ? 'text-xs' : 'text-sm'} font-semibold`}>
              {isDragging ? 'Drop now!' : getLabel()}
            </p>
            {acceptVideo && !compact && (
              <p className="text-xs text-gray-300 mt-1">Video max: {MAX_VIDEO_SIZE_MB}MB</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
